/* eslint-disable import/first */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
// Removed CustomerAuthProvider - merchant app only uses SecureAuthProvider
import './styles/globals.css';
import './styles/viewTransitions.css';
import { getPlatformDomainName } from './config/platformConfig';
import SimpleRegister from './features/auth/SimpleRegister';
import SimpleLogin from './features/auth/SimpleLogin';
import ForgotPassword from './features/auth/ForgotPassword';
import PasswordReset from './features/auth/PasswordReset';
import Products from './features/products/Products';
import ProductFormPage from './features/products/ProductFormPage';
import CreateStore from './features/store/CreateStore';
import ManageStore from './features/store/ManageStore';
import StorefrontLayout from './components/layout/StorefrontLayout';
// Store Management Pages
import StoreInformation from './features/storeManagement/StoreInformation';
import SiteCustomization from './features/storeManagement/SiteCustomization';
import ViewStore from './features/storeManagement/ViewStore';
import PageBuilder from './features/storeManagement/PageBuilder';
import StorefrontSettings from './features/storeManagement/StorefrontSettings';
// Theme Marketplace & Builder
import ThemePreview from './features/themes/ThemePreview';
import ThemeBuilder from './features/themes/ThemeBuilder';
import ThemePurchaseCallback from './features/themes/ThemePurchaseCallback';
import HomePage from './pages/storefront/HomePage';
import AbandonedRecoveryPage from './pages/storefront/AbandonedRecoveryPage';
import CheckoutLinkLanding from './pages/storefront/CheckoutLinkLanding';
import CustomPage from './pages/storefront/CustomPage';
import AboutPage from './pages/storefront/AboutPage';
import ContactPage from './pages/storefront/ContactPage';
// Appointments disabled per request
import ShopPage from './pages/storefront/ShopPage';
import CategoryPage from './pages/storefront/CategoryPage';
import CollectionsPage from './pages/storefront/CollectionsPage';
import CollectionDetailPage from './pages/storefront/CollectionDetailPage';
import AdvertisePage from './pages/storefront/AdvertisePage';
import CartPage from './features/cart/CartPage';
import Checkout from './features/checkout/CheckoutNew';
import CheckoutProtection from './components/CheckoutProtection';
import OrderConfirmation from './features/checkout/OrderConfirmation';
import POSPayment from './features/checkout/POSPayment';
import AppointmentConfirmation from './components/AppointmentConfirmation';
import PayInvoice from './features/billing/PayInvoice';
import RecurringInvoices from './features/billing/RecurringInvoices';
import MerchantInvoices from './features/billing/MerchantInvoices';
import ProductPage from './features/products/ProductPage';
import NotFound from './components/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './features/dashboard/Dashboard';
import Overview from './features/overview/Overview';
import AbandonedCarts from './features/abandoned/AbandonedCarts';
import Orders from './features/orders/Orders';
import OrderDetailsPage from './features/orders/OrderDetailsPage';
import POSv2 from './features/pos/POSv2';
import ShiftAnalytics from './features/pos/ShiftAnalytics';
import POSActivityLogs from './features/pos/POSActivityLogs';
// POSServiceChargeLog retired — consolidated into the unified wallet page.
// Step 10 will delete the source file outright; for now it stays in the
// repo so /pos/service-charges redirects below stay diff-free.
import RefundRequests from './features/refunds/RefundRequests';
import ReturnRequests from './features/returnRequests/ReturnRequests';
import CustomDomains from './features/customDomains/CustomDomains';
import Customers from './features/customers/Customers';
import TagsSegmentsManagement from './features/tagsSegments/TagsSegmentsManagement';
import DiscountsNew from './features/discounts/DiscountsNew';
import Expenses from './features/expenses/Expenses';
import TeamManagement from './features/team/TeamManagement';
import TeamActivityLogs from './features/team/TeamActivityLogs';
import AcceptInvitation from './features/team/AcceptInvitation';
import NewOnboardingWizard from './features/onboarding/NewOnboardingWizard';
import Media from './features/media/Media';
import Pages from './features/pages/Pages';
import Campaigns from './features/campaigns/Campaigns';
// import EmailTemplates from './features/emailTemplates/EmailTemplates'; // Removed - not used in merchant app
// Removed merchant-only Gmail OAuth page
import ShippingZones from './features/shippingZones/ShippingZones';
import CustomerDashboard from './features/customerDashboard/CustomerDashboard';
import CustomerAuth from './features/customerAuth/CustomerAuth';
// Settlements + DvaTransactions retired — consolidated into the unified
// wallet page. Source files remain for Step 10 cutover.
import Wallet from './features/wallet/Wallet';
import Finance from './features/finance/Finance';
import Profile from './features/profile/Profile';
import BusinessCardPage from './features/businessCard/BusinessCardPage';
import Settings from './features/settings/Settings';
import Locations from './features/locations/Locations';
import MerchantKYC from './features/kyc/MerchantKYC';
import EmailVerification from './features/auth/EmailVerification';
import OTPVerification from './features/auth/OTPVerification';
import VerificationRequired from './features/auth/VerificationRequired';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMyStore } from './features/store/storeSlice';
// Removed useCustomerAuth - merchant app only uses useSecureAuth
import { SecureAuthProvider, useSecureAuth } from './contexts/SecureAuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import MerchantNotifications from './features/notifications/MerchantNotifications';
import NotificationPreferences from './features/notifications/NotificationPreferences';
import useMerchantStatus from './hooks/useMerchantStatus';
import AccountStatusError from './components/AccountStatusError';
import EmailTemplates from './features/emailTemplates/EmailTemplates';
import MerchantEmailCampaigns from './features/emailCampaigns/MerchantEmailCampaigns';
import EmailCampaigns from './features/emailCampaigns/EmailCampaigns';
import { WhatsAppPage } from './features/whatsapp';
import WhatsAppSetup from './features/whatsapp/WhatsAppSetup';
import WhatsAppAssistantHandoff from './features/whatsapp/WhatsAppAssistantHandoff';
import LaunchBoost from './features/whatsapp/LaunchBoost';
import StorefrontChat from './features/storefront-chat/StorefrontChat';
import StorefrontChatLive from './features/storefront-chat/LiveActivity';
import StorefrontChatSettings from './features/storefront-chat/Settings';
import SMSPage from './features/sms/SMSPage';
import FeatureGate from './components/FeatureGate';
import ProtectedPage from './components/ProtectedPage';
import SubscriptionVerify from './features/subscription/SubscriptionVerify';
import UpgradePage from './features/subscription/UpgradePage';
import UpgradeCheckoutPage from './features/subscription/UpgradeCheckoutPage';
import OfferPage from './features/subscription/OfferPage';
import MerchantAppointments from './features/appointments/MerchantAppointments';
import MerchantAds from './features/ads/MerchantAds';
import DeepLinks from './features/deepLinks/DeepLinks';
import DeepLinkDetail from './features/deepLinks/DeepLinkDetail';
import Affiliates from './features/affiliates/Affiliates';
import AffiliateDetail from './features/affiliates/AffiliateDetail';
import AffiliatePortal from './features/affiliates/AffiliatePortal';
import { BrandCommunity } from './features/brand-community';
import CommunityRedirect from './features/community/CommunityRedirect';
import MarketerPortal from './features/marketer/MarketerPortal';
import KnowledgeBase from './features/knowledgebase/KnowledgeBase';
import KnowledgeBaseArticle from './features/knowledgebase/KnowledgeBaseArticle';
import Collections from './features/collections/Collections';
import InventoryReportPage from './features/products/InventoryReportPage';
import ReferralDashboard from './features/referral/ReferralDashboard';
import { Categories } from './features/categories';
import CalculatorPage from './features/utilities/calculator/CalculatorPage';
import NotesPage from './features/utilities/notes/NotesPage';
import TaskPlannerPage from './features/utilities/tasks/TaskPlannerPage';
import Loader from './components/ui/Loader';
import DigitalDownload from './pages/DigitalDownload';
import SharedCollectionPage from './pages/SharedCollectionPage';
import InvoicePage from './pages/InvoicePage';
import ReceiptPage from './pages/ReceiptPage';
import WhatsAppOrderPage from './pages/WhatsAppOrderPage';
import MarketplaceHomePage from './features/marketplace/MarketplaceHomePage';
import MarketplaceMerchantPage from './features/marketplace/MarketplaceMerchantPage';
import MarketplaceProductPage from './features/marketplace/MarketplaceProductPage';
import MarketplaceMerchantsPage from './features/marketplace/MarketplaceMerchantsPage';
import MerchantMarketplaceHub from './features/marketplace/MerchantMarketplaceHub';

const InvoiceRoute = () => {
  const location = useLocation();
  const hasOrderInvoiceToken = new URLSearchParams(location.search).has('token');
  return hasOrderInvoiceToken ? <InvoicePage /> : <PayInvoice />;
};

// Lazy load Analytics components to avoid loading on storefront
const Analytics = React.lazy(() => import('./features/analytics/Analytics'));
const AIAnalyticsPage = React.lazy(() => import('./features/analytics/AIAnalyticsPage'));
// Auth Loading Screen Component
// Shows during initial auth check to prevent white flash
// Uses inline styles to ensure immediate rendering before CSS loads
const AuthLoadingScreen = () => {
  // Inline keyframe animation for the dots
  const dotAnimation = `
    @keyframes authLoadingPulse {
      0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
      40% { opacity: 1; transform: scale(1); }
    }
  `;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#FFFFFF',
      zIndex: 9999,
    }}>
      <style>{dotAnimation}</style>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
      }}>
        {/* Three dot loading indicator */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#0D7463',
                animation: 'authLoadingPulse 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: '#6B7280',
          fontWeight: 500,
          letterSpacing: '0.5px',
        }}>
          Loading...
        </p>
      </div>
    </div>
  );
};

// Protected Routes Component
const ProtectedRoutes = () => {
  const { isAuthenticated, user, loading: authLoading } = useSecureAuth();

  // Check if there's an impersonation token in the URL - if so, don't redirect yet
  const urlParams = new URLSearchParams(window.location.search);
  const hasImpersonationToken = urlParams.has('impersonate_token');

  if (authLoading || hasImpersonationToken) {
    return <AuthLoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user needs to complete onboarding
  // Skip this check for team members (they have merchantRole but aren't store owners)
  const isTeamMember = user?.merchantRole && user?.merchantRole !== 'OWNER';
  const hasStore = user?.ownsStore || user?.ownedStoreId || user?.storeId;

  // Redirect to onboarding if user doesn't have a store yet
  // Store creation is the key milestone - once they have a store, they can access the dashboard
  if (!isTeamMember && !hasStore) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <Routes>
      {/* Overview — primary landing surface (welcome + KYC + setup checklist + store URL).
          Dashboard is now KPIs-only; Overview handles lifecycle/onboarding visibility. */}
      <Route path="/overview" element={<Overview />} />

      {/* Dashboard - accessible to all roles */}
      <Route path="/dashboard" element={<Dashboard />} />

      {/* Products - role: products:view */}
      <Route path="/products" element={
        <ProtectedPage permission="products:view" title="Products">
          <Products />
        </ProtectedPage>
      } />

      <Route path="/products/inventory-report" element={
        <ProtectedPage permission="products:view" title="Inventory Report">
          <InventoryReportPage />
        </ProtectedPage>
      } />

      {/* Add / edit product — full page, replaces the slide-in sidebar.
          Backend enforces granular create/edit permissions on the API; this
          page-level guard just matches the list page's `products:view`. */}
      <Route path="/products/new" element={
        <ProtectedPage permission="products:view" title="Add Product">
          <ProductFormPage />
        </ProtectedPage>
      } />
      <Route path="/products/:id/edit" element={
        <ProtectedPage permission="products:view" title="Edit Product">
          <ProductFormPage />
        </ProtectedPage>
      } />

      {/* Categories - role: products:view */}
      <Route path="/categories" element={
        <ProtectedPage permission="products:view" title="Categories">
          <Categories />
        </ProtectedPage>
      } />

      {/* Collections - role: products:view */}
      <Route path="/collections" element={
        <ProtectedPage permission="products:view" title="Collections">
          <Collections />
        </ProtectedPage>
      } />

      {/* Orders - role: orders:view */}
      <Route path="/orders" element={
        <ProtectedPage permission="orders:view" title="Orders">
          <Orders />
        </ProtectedPage>
      } />
      <Route path="/orders/:id" element={
        <ProtectedPage permission="orders:view" title="Order details">
          <OrderDetailsPage />
        </ProtectedPage>
      } />

      {/* POS - Point of Sale (Salesive-inspired rebuild, formerly /pos/v2).
          The legacy POS.js still lives in features/pos/ but is unmounted;
          archive after a couple of clean prod cycles. */}
      <Route path="/pos" element={
        <ProtectedPage permission="orders:edit" title="Point of Sale">
          <POSv2 />
        </ProtectedPage>
      } />

      {/* Shift Analytics - Premium Feature */}
      <Route path="/shift-analytics" element={
        <ProtectedPage permission="orders:view" title="Shift Analytics">
          <ShiftAnalytics />
        </ProtectedPage>
      } />

      {/* POS Activity Logs */}
      <Route path="/pos/activity" element={
        <ProtectedPage permission="orders:view" title="POS Activity Logs">
          <POSActivityLogs />
        </ProtectedPage>
      } />

      {/* POS Service Charge Log — retired (consolidated into /wallet).
          Old bookmarks redirect; filter the wallet activity by category
          POS_FEE_ACCRUAL to see the same data. */}
      <Route path="/pos/service-charges" element={<Navigate to="/wallet" replace />} />

      {/* Refunds - role: refunds:view */}
      <Route path="/refunds" element={
        <ProtectedPage permission="refunds:view" title="Refunds">
          <RefundRequests />
        </ProtectedPage>
      } />

      {/* Return Requests - role: orders:view */}
      <Route path="/return-requests" element={
        <ProtectedPage permission="orders:view" title="Return Requests">
          <ReturnRequests />
        </ProtectedPage>
      } />

      {/* Customers - role: customers:view */}
      <Route path="/customers" element={
        <ProtectedPage permission="customers:view" title="Customers">
          <Customers />
        </ProtectedPage>
      } />

      {/* Tags & Segments - role: customers:view, feature: marketing.tags */}
      <Route path="/tags-segments" element={
        <ProtectedPage permission="customers:view" title="Tags & Segments">
          <FeatureGate featureId="marketing.tags">
            <TagsSegmentsManagement />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Discounts - role: discounts:view, feature: marketing.discounts */}
      <Route path="/discounts" element={
        <ProtectedPage permission="discounts:view" title="Discounts">
          <FeatureGate featureId="marketing.discounts">
            <DiscountsNew />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Expenses - role: expenses:view */}
      <Route path="/expenses" element={
        <ProtectedPage permission="expenses:view" title="Expenses">
          <Expenses />
        </ProtectedPage>
      } />

      {/* Team - role: team:view */}
      <Route path="/team" element={
        <ProtectedPage permission="team:view" title="Team Management">
          <TeamManagement />
        </ProtectedPage>
      } />

      {/* Team Activity Logs - role: team:view (feature gated to PRO/PREMIUM) */}
      <Route path="/team/activity" element={
        <ProtectedPage permission="team:view" title="Team Activity Logs">
          <TeamActivityLogs />
        </ProtectedPage>
      } />

      {/* Settings - role: settings:view */}
      <Route path="/settings" element={
        <ProtectedPage permission="settings:view" title="Settings">
          <Settings />
        </ProtectedPage>
      } />

      {/* Locations - role: settings:view */}
      <Route path="/locations" element={
        <ProtectedPage permission="settings:view" title="Locations">
          <Locations />
        </ProtectedPage>
      } />

      {/* Business Card Generator */}
      <Route path="/business-card" element={
        <ProtectedPage permission="settings:view" title="Business Card Generator">
          <BusinessCardPage />
        </ProtectedPage>
      } />

      {/* KYC - role: kyc:view */}
      <Route path="/kyc" element={
        <ProtectedPage permission="kyc:view" title="KYC Verification">
          <MerchantKYC />
        </ProtectedPage>
      } />

      {/* Analytics - role: analytics:view, feature: analytics.sales */}
      <Route path="/analytics" element={
        <ProtectedPage permission="analytics:view" title="Analytics">
          <FeatureGate featureId="analytics.sales">
            <React.Suspense fallback={<div style={{padding: '20px', textAlign: 'center'}}>Loading analytics...</div>}>
              <Analytics />
            </React.Suspense>
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* AI Analytics - role: analytics:view, feature: ai.insights */}
      <Route path="/ai-analytics" element={
        <ProtectedPage permission="analytics:view" title="AI Analytics">
          <FeatureGate featureId="ai.insights">
            <React.Suspense fallback={<div style={{padding: '20px', textAlign: 'center'}}>Loading AI analytics...</div>}>
              <AIAnalyticsPage />
            </React.Suspense>
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Media - role: media:view */}
      <Route path="/media" element={
        <ProtectedPage permission="media:view" title="Media Library">
          <Media />
        </ProtectedPage>
      } />

      {/* Pages - role: store:view, feature: store.themes */}
      <Route path="/pages" element={
        <ProtectedPage permission="store:view" title="Pages">
          <FeatureGate featureId="store.themes">
            <Pages />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Campaigns - role: campaigns:view */}
      <Route path="/campaigns" element={
        <ProtectedPage permission="campaigns:view" title="Campaigns">
          <Campaigns />
        </ProtectedPage>
      } />

      {/* Email Templates - role: campaigns:view, feature: marketing.email */}
      <Route path="/email-templates" element={
        <ProtectedPage permission="campaigns:view" title="Email Templates">
          <FeatureGate featureId="marketing.email">
            <EmailTemplates />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Email Campaigns - role: campaigns:view, feature: marketing.email */}
      <Route path="/email-campaigns" element={
        <ProtectedPage permission="campaigns:view" title="Email Campaigns">
          <FeatureGate featureId="marketing.email">
            <EmailCampaigns />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Shipping Zones - role: shipping:view */}
      <Route path="/shipping-zones" element={
        <ProtectedPage permission="shipping:view" title="Shipping Zones">
          <ShippingZones />
        </ProtectedPage>
      } />

      {/* Domains - role: store:view. The page is available to every
          merchant because Revstar URL editing is not a paid feature;
          custom-domain actions are gated inside the page. */}
      <Route path="/custom-domains" element={
        <ProtectedPage permission="store:view" title="Domains">
          <CustomDomains />
        </ProtectedPage>
      } />

      {/* Settlements + Bank Transfers — retired (consolidated into /wallet).
          Old bookmarks redirect; filter the wallet by Source = Bank transfer
          (DVA) / Online checkout (Paystack) / Online checkout (Flutterwave)
          to see the same data with the platform-fee breakdown. */}
      <Route path="/settlements"    element={<Navigate to="/wallet" replace />} />
      <Route path="/bank-transfers" element={<Navigate to="/wallet" replace />} />

      {/* Wallet - role: settlements:view */}
      <Route path="/wallet" element={
        <ProtectedPage permission="settlements:view" title="Wallet">
          <Wallet />
        </ProtectedPage>
      } />

      {/* Finance — role: finance:view */}
      <Route path="/finance" element={
        <ProtectedPage permission="finance:view" title="Finance">
          <Finance />
        </ProtectedPage>
      } />

      {/* Billing — recurring invoice templates (retainer billing).
          Tier-gated to PRO+PREMIUM via FeatureGate inside the page. */}
      <Route path="/billing/recurring-invoices" element={
        <ProtectedPage permission="orders:view" title="Recurring Invoices">
          <RecurringInvoices />
        </ProtectedPage>
      } />

      {/* Billing — issued invoices index (both template-spawned and
          one-off). Same tier gate as templates. */}
      <Route path="/billing/invoices" element={
        <ProtectedPage permission="orders:view" title="Invoices">
          <MerchantInvoices />
        </ProtectedPage>
      } />

      {/* Profile - accessible to all */}
      <Route path="/profile" element={<Profile />} />

      {/* Store Management Pages */}
      <Route path="/manage-store" element={<Navigate to="/manage-store/information" replace />} />

      {/* Store Information - role: store:view */}
      <Route path="/manage-store/information" element={
        <ProtectedPage permission="store:view" title="Store Information">
          <StoreInformation />
        </ProtectedPage>
      } />

      {/* Customization - role: store:view, feature: store.customization */}
      <Route path="/manage-store/customization" element={
        <ProtectedPage permission="store:view" title="Store Customization">
          <FeatureGate featureId="store.customization">
            <SiteCustomization />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Page Builder - role: store:view */}
      <Route path="/manage-store/page-builder" element={
        <ProtectedPage permission="store:view" title="Page Builder">
          <PageBuilder />
        </ProtectedPage>
      } />

      {/* Theme Marketplace was a standalone page; it's now unified into
          /manage-store/customization (Premium themes tab). Redirect any
          old bookmarks / external links. */}
      <Route path="/site/themes" element={<Navigate to="/manage-store/customization" replace />} />

      {/* Theme Preview - role: store:view */}
      <Route path="/site/themes/:slug/preview" element={
        <ProtectedPage permission="store:view" title="Theme Preview">
          <ThemePreview />
        </ProtectedPage>
      } />

      {/* Theme Builder - role: store:edit */}
      <Route path="/site/themes/builder" element={
        <ProtectedPage permission="store:edit" title="Theme Builder">
          <FeatureGate featureId="store.themes">
            <ThemeBuilder />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Theme Purchase Callback - handles Paystack redirect */}
      <Route path="/site/themes/purchase/callback" element={
        <ProtectedPage permission="store:view" title="Payment Verification">
          <ThemePurchaseCallback />
        </ProtectedPage>
      } />

      {/* Storefront Settings - role: store:view */}
      <Route path="/manage-store/storefront-settings" element={
        <ProtectedPage permission="store:view" title="Storefront Settings">
          <StorefrontSettings />
        </ProtectedPage>
      } />

      {/* Domain Management - role: store:view. Render the same broader
          Domain page so URL editing and custom domains live together. */}
      <Route path="/manage-store/domain" element={
        <ProtectedPage permission="store:view" title="Domain Management">
          <CustomDomains />
        </ProtectedPage>
      } />

      {/* View Store - role: store:view */}
      <Route path="/manage-store/view" element={
        <ProtectedPage permission="store:view" title="View Store">
          <ViewStore />
        </ProtectedPage>
      } />

      {/* Abandoned Carts - role: orders:view, feature: marketing.abandoned_carts */}
      <Route path="/abandoned-carts" element={
        <ProtectedPage permission="orders:view" title="Abandoned Carts">
          <FeatureGate featureId="marketing.abandoned_carts">
            <AbandonedCarts />
          </FeatureGate>
        </ProtectedPage>
      } />
      <Route path="/abandoned" element={<Navigate to="/abandoned-carts" replace />} />

      {/* Notifications - accessible to all */}
      <Route path="/notifications" element={<MerchantNotifications />} />
      <Route path="/notifications/preferences" element={<NotificationPreferences />} />

      {/* WhatsApp - role: whatsapp:view, feature: marketing.whatsapp */}
      {/* Note: WhatsAppPage includes Shell and FeatureGate internally */}
      <Route path="/whatsapp" element={
        <ProtectedPage permission="whatsapp:view" title="WhatsApp">
          <WhatsAppPage />
        </ProtectedPage>
      } />

      {/* Storefront AI Chatbot — inbox + thread.
          Component includes Shell + FeatureGate internally. Conversation id
          (when present) drives the right-pane selection. */}
      <Route path="/storefront-chat" element={
        <ProtectedPage permission="chat:view:own" title="Storefront chat">
          <StorefrontChat />
        </ProtectedPage>
      } />
      <Route path="/storefront-chat/live" element={
        <ProtectedPage permission="messages:view:own" title="Storefront chat — live">
          <StorefrontChatLive />
        </ProtectedPage>
      } />
      <Route path="/storefront-chat/settings" element={
        <ProtectedPage permission="chat:settings:edit" title="Storefront chat — settings">
          <StorefrontChatSettings />
        </ProtectedPage>
      } />
      <Route path="/storefront-chat/:conversationId" element={
        <ProtectedPage permission="chat:view:own" title="Storefront chat">
          <StorefrontChat />
        </ProtectedPage>
      } />
      <Route path="/whatsapp/setup" element={
        <ProtectedPage permission="whatsapp:view" title="WhatsApp Setup">
          <FeatureGate featureId="marketing.whatsapp">
            <WhatsAppSetup />
          </FeatureGate>
        </ProtectedPage>
      } />
      <Route path="/whatsapp/launch-boost" element={
        <ProtectedPage permission="whatsapp:view" title="Launch Boost">
          <LaunchBoost />
        </ProtectedPage>
      } />

      {/* SMS - role: customers:view, feature: marketing.sms */}
      <Route path="/sms" element={
        <ProtectedPage permission="customers:view" title="SMS Notifications">
          <FeatureGate featureId="marketing.sms">
            <SMSPage />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Deep Links - feature: marketing.deep_links */}
      <Route path="/deep-links" element={
        <ProtectedPage permission="store:manage" title="Deep Links">
          <FeatureGate featureId="marketing.deep_links">
            <DeepLinks />
          </FeatureGate>
        </ProtectedPage>
      } />
      <Route path="/deep-links/:id" element={
        <ProtectedPage permission="store:manage" title="Deep Link Details">
          <FeatureGate featureId="marketing.deep_links">
            <DeepLinkDetail />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Affiliates - feature: marketing.affiliates */}
      <Route path="/affiliates" element={
        <ProtectedPage permission="store:manage" title="Affiliates">
          <FeatureGate featureId="marketing.affiliates">
            <Affiliates />
          </FeatureGate>
        </ProtectedPage>
      } />
      <Route path="/affiliates/:id" element={
        <ProtectedPage permission="store:manage" title="Affiliate Details">
          <FeatureGate featureId="marketing.affiliates">
            <AffiliateDetail />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Platform Referrals - Merchants can refer other merchants */}
      <Route path="/referrals" element={
        <ProtectedPage permission="store:view" title="Referrals">
          <ReferralDashboard />
        </ProtectedPage>
      } />

      {/* Marketplace - merchant-side distribution hub */}
      <Route path="/merchant-marketplace/overview" element={
        <ProtectedPage permission="store:view" title="Marketplace">
          <MerchantMarketplaceHub initialTab="overview" />
        </ProtectedPage>
      } />
      <Route path="/merchant-marketplace/boosts" element={
        <ProtectedPage permission="products:view" title="Boost Products">
          <MerchantMarketplaceHub initialTab="boosts" />
        </ProtectedPage>
      } />
      <Route path="/merchant-marketplace/reports" element={
        <ProtectedPage permission="store:view" title="Marketplace Reports">
          <MerchantMarketplaceHub initialTab="reports" />
        </ProtectedPage>
      } />

      {/* Brand Community - feature: marketing.brandCommunity */}
      <Route path="/brand-community" element={
        <ProtectedPage permission="store:manage" title="Brand Community">
          <FeatureGate featureId="marketing.brandCommunity">
            <BrandCommunity />
          </FeatureGate>
        </ProtectedPage>
      } />

      {/* Community Hub SSO Redirect */}
      <Route path="/community" element={
        <ProtectedPage title="Community">
          <CommunityRedirect />
        </ProtectedPage>
      } />

      {/* Appointments - role: orders:view */}
      <Route path="/appointments" element={
        <ProtectedPage permission="orders:view" title="Appointments">
          <MerchantAppointments />
        </ProtectedPage>
      } />

      {/* Storefront Ads - role: store:manage */}
      <Route path="/ads" element={
        <ProtectedPage permission="store:manage" title="Storefront Ads">
          <MerchantAds />
        </ProtectedPage>
      } />

{/* Utilities — quick tools (calculator, notes, tasks). Each has a
          page route and is also mountable as a modal from anywhere.
          (Standalone /calendar route removed — Google Calendar sync now
          lives only inside Settings → Integrations; task/calendar
          planner lives at /utilities/tasks.) */}
      <Route path="/utilities/calculator" element={<CalculatorPage />} />
      <Route path="/utilities/notes" element={<NotesPage />} />
      <Route path="/utilities/tasks" element={<TaskPlannerPage />} />

      {/* Upgrade - role: payments:view */}
      <Route path="/upgrade" element={
        <ProtectedPage permission="payments:view" title="Upgrade Plan">
          <UpgradePage />
        </ProtectedPage>
      } />
      <Route path="/upgrade/checkout" element={
        <ProtectedPage permission="payments:view" title="Subscription Summary">
          <UpgradeCheckoutPage />
        </ProtectedPage>
      } />

      {/* Offer Page - Special promo/discount offers */}
      <Route path="/offer" element={
        <ProtectedPage permission="payments:view" title="Special Offer">
          <OfferPage />
        </ProtectedPage>
      } />

      {/* Catch-all for unknown routes - show 404 page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Conditional Store Component
const StoreComponent = () => {
  const store = useSelector(state => state.store?.current);
  const storeStatus = useSelector(state => state.store?.status);
  const { user, isAuthenticated, loading: authLoading, getAxios } = useSecureAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    // Token and store status logged for debugging (removed sensitive data)
    if (isAuthenticated && user && !store && storeStatus !== 'loading' && storeStatus !== 'failed') {
      console.log('Dispatching fetchMyStore...');
      // Use the secure auth axios instance
      const axios = getAxios();
      dispatch(fetchMyStore(axios));
    }
  }, [isAuthenticated, user, store, storeStatus, dispatch, getAxios]);

  // Show loading state while authenticating or fetching store
  if (authLoading || storeStatus === 'loading') {
    return <AuthLoadingScreen />;
  }

  // Show error state if fetch failed
  if (storeStatus === 'failed') {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        <div>Error loading store. Please try again.</div>
        <button 
          onClick={() => window.location.reload()} 
          style={{ 
            marginTop: '1rem', 
            padding: '0.5rem 1rem', 
            backgroundColor: '#2563eb', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (store) {
    return <ManageStore />;
  } else {
    return <CreateStore />;
  }
};

// Helper to get subdomain slug from hostname
const getSubdomainSlug = () => {
  const hostname = window.location.hostname;
  const platformDomain = getPlatformDomainName();
  const platformDomainWithoutPort = platformDomain.split(':')[0];

  // Debug logging
  console.log('getSubdomainSlug Debug:', {
    hostname,
    platformDomain,
    platformDomainWithoutPort,
    cleanHostname: hostname.replace(/^www\./, ''),
    includesDot: hostname.includes('.'),
    endsWithPlatform: hostname.endsWith(platformDomainWithoutPort)
  });

  // Reserved subdomains that should NOT be treated as store slugs
  const reservedSubdomains = ['admin', 'api', 'www', 'app', 'dashboard', 'console', 'my', 'cdn', 'static', 'staging', 'dev', 'test', 'pay', 'marketplace'];

  // Remove www. if present
  const cleanHostname = hostname.replace(/^www\./, '');

  // Check if this is a subdomain (not the base domain)
  if (cleanHostname.includes('.') && cleanHostname.endsWith(platformDomainWithoutPort)) {
    // Extract subdomain
    const subdomain = cleanHostname.substring(0, cleanHostname.length - platformDomainWithoutPort.length - 1);

    console.log('getSubdomainSlug: Extracted subdomain:', subdomain);

    // For multi-level subdomains (e.g., dashboard.staging.userevstar.com),
    // check if the FIRST part is reserved (e.g., "dashboard" in "dashboard.staging")
    const firstSubdomainPart = subdomain.split('.')[0];

    // Return subdomain if the first part is not reserved
    if (subdomain && !reservedSubdomains.includes(firstSubdomainPart)) {
      console.log('getSubdomainSlug: Returning subdomain:', subdomain);
      return subdomain;
    }

    console.log('getSubdomainSlug: Subdomain is reserved:', firstSubdomainPart);
  }

  console.log('getSubdomainSlug: No subdomain detected, returning null');
  return null;
};

const isMarketplaceHost = () => {
  const hostname = window.location.hostname.replace(/^www\./, '');
  const platformDomain = getPlatformDomainName().split(':')[0];

  if (hostname === 'marketplace.localhost') {
    return true;
  }

  return hostname === `marketplace.${platformDomain}`;
};

const MarketplaceShell = () => {
  return (
    <Routes>
      <Route path="/" element={<MarketplaceHomePage />} />
      <Route path="/merchants" element={<MarketplaceMerchantsPage />} />
      <Route path="/merchant/:slug" element={<MarketplaceMerchantPage />} />
      <Route path="/product/:storeSlug/:productSlug" element={<MarketplaceProductPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Helper to detect if current hostname is a custom domain
const isCustomDomain = () => {
  const hostname = window.location.hostname;
  const platformDomain = getPlatformDomainName();
  const platformDomainWithoutPort = platformDomain.split(':')[0];

  // Check if we're on a custom domain (not the platform domain)
  const isCustom = !hostname.includes(platformDomainWithoutPort) &&
                   !hostname.includes('localhost') &&
                   !hostname.includes('127.0.0.1') &&
                   !hostname.includes('vercel.app');

  console.log('isCustomDomain Debug:', {
    hostname,
    platformDomainWithoutPort,
    isCustom
  });

  return isCustom;
};

// Subdomain/Custom Domain Storefront Wrapper - renders StorefrontLayout at root
const SubdomainStorefront = () => {
  const slug = getSubdomainSlug();
  const customDomain = isCustomDomain();

  console.log('🔵 SubdomainStorefront: slug detected:', slug);
  console.log('🔵 SubdomainStorefront: customDomain detected:', customDomain);
  console.log('🔵 SubdomainStorefront: current path:', window.location.pathname);

  // Render storefront for either subdomain OR custom domain
  if (slug || customDomain) {
    return (
      <Routes>
        {/* Redirect /login on subdomain/custom domain to customer login */}
        <Route path="/login" element={<CustomerAuth />} />

        {/* Payment verification route (outside StorefrontLayout) */}
        <Route path="/payment/verify" element={<OrderConfirmation />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />
        <Route path="/appointment/confirmation" element={<AppointmentConfirmation />} />

        {/* Public invoices: legacy order invoice uses ?token=, billing invoice uses path token. */}
        <Route path="/invoice/:invoiceRef" element={<InvoiceRoute />} />

        {/* POS Payment route - for card payments from POS */}
        <Route path="/checkout/pay/:orderId" element={<POSPayment />} />

        {/* Digital Download route */}
        <Route path="/download/:accessToken" element={<DigitalDownload />} />

        {/* Shared Collection route - public access with token */}
        <Route path="/share/collection/:token" element={<SharedCollectionPage />} />

        {/* Receipt routes - public access with token */}
        <Route path="/receipt/:orderId" element={<ReceiptPage />} />

        {/* WhatsApp Order route - public access */}
        <Route path="/wa/:whatsappOrderId" element={<WhatsAppOrderPage />} />

        {/* Affiliate Portal - public access */}
        <Route path="/affiliate/:referralCode" element={<AffiliatePortal />} />

        {/* Marketer Portal - public access (marketers have own auth) */}
        <Route path="/marketer" element={<MarketerPortal />} />

        {/* Storefront routes */}
        <Route path="/" element={<StorefrontLayout />}>
          <Route index element={<HomePage />} />
          <Route path="shop" element={<ShopPage />} />
          <Route path="category/:category" element={<CategoryPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="advertise" element={<AdvertisePage />} />
          <Route path="collections" element={<CollectionsPage />} />
          <Route path="collection/:collectionSlug" element={<CollectionDetailPage />} />
          <Route path="product/:productSlug" element={<ProductPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="recover/abandoned/:id" element={<AbandonedRecoveryPage />} />
          <Route path="checkout/link/:token" element={<CheckoutLinkLanding />} />
          <Route path=":pageSlug" element={<CustomPage />} />
        </Route>

        {/* Catch-all: show 404 page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  return null;
};

// Main App Content Component
const AppContent = () => {
  const store = useSelector(state => state.store?.current);
  const dispatch = useDispatch();
  const { loading: authLoading } = useSecureAuth();
  const location = useLocation();

  // Only check merchant status (no subdomain check needed here anymore)
  const merchantStatusError = useMerchantStatus();

  // App component rendered (removed sensitive data logging)

  // Removed Redux auth actions - using secure auth context instead
  // useEffect(() => {
  //   if (token && !store?.slug) {
  //     dispatch(fetchMyStore(token));
  //   }
  // }, [token, store?.slug, dispatch]);

  // Load cart from backend on app initialization
  // useEffect(() => {
  //   dispatch(fetchCart(customer?._id));
  // }, [dispatch, customer?._id]);

  // Check if current path is a public route (doesn't need auth loading screen)
  const publicPaths = ['/login', '/register', '/signup', '/forgot-password', '/reset-password', '/onboarding', '/verify-email', '/verify-otp'];
  const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path)) ||
    location.pathname.startsWith('/s/') || // Storefront routes
    location.pathname.startsWith('/customer/') ||
    location.pathname.startsWith('/team/accept/') ||
    location.pathname.startsWith('/affiliate/') ||
    location.pathname.startsWith('/marketer') ||
    location.pathname.startsWith('/download/') ||
    location.pathname.startsWith('/invoice/') ||
    location.pathname.startsWith('/receipt/') ||
    location.pathname.startsWith('/wa/') ||
    location.pathname.startsWith('/payment/') ||
    location.pathname.startsWith('/order-confirmation') ||
    location.pathname.startsWith('/appointment/') ||
    location.pathname.startsWith('/checkout') ||
    location.pathname.startsWith('/cart');

  // Show loading screen for non-public routes during auth check
  if (authLoading && !isPublicPath) {
    return <AuthLoadingScreen />;
  }

  // If merchant is banned/suspended, show the error component (only for merchant app)
  if (merchantStatusError) {
    return (
      <AccountStatusError
        error={merchantStatusError.error}
        message={merchantStatusError.message}
        reason={merchantStatusError.reason}
        bannedAt={merchantStatusError.bannedAt}
        suspendedAt={merchantStatusError.suspendedAt}
        appealEmail={merchantStatusError.appealEmail}
        appealSubject={merchantStatusError.appealSubject}
      />
    );
  }

  return (
    // Routes only; providers are applied at the App() level
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<SimpleLogin />} />
      <Route path="/register" element={<SimpleRegister />} />
      <Route path="/signup" element={<SimpleRegister />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      {/* Onboarding - Semi-public route (accessible from email links) */}
      <Route path="/onboarding" element={<NewOnboardingWizard />} />
          {/* Storefront Layout Routes */}
          <Route path="/s/:slug" element={<StorefrontLayout />}>
            <Route index element={<HomePage />} />
            <Route path="shop" element={<ShopPage />} />
            <Route path="category/:category" element={<CategoryPage />} />
            <Route path="collections" element={<CollectionsPage />} />
            <Route path="collection/:collectionSlug" element={<CollectionDetailPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="advertise" element={<AdvertisePage />} />
            <Route path="product/:productSlug" element={<ProductPage />} />
            <Route path="cart" element={<CartPage />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="recover/abandoned/:id" element={<AbandonedRecoveryPage />} />
            {/* Render custom pages by slug */}
            <Route path=":pageSlug" element={<CustomPage />} />
          </Route>
          {/* Payment and order routes */}
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/payment/verify" element={<OrderConfirmation />} />
          <Route path="/appointment/confirmation" element={<AppointmentConfirmation />} />
          {/* Token-based verification — used by /resend-verification emails (legacy mobile path). New web signup uses /verify-otp. */}
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/verify-otp" element={<OTPVerification />} />
          <Route path="/verification-required" element={<VerificationRequired />} />
          <Route path="/wa-assistant/handoff/:token" element={<WhatsAppAssistantHandoff />} />
          <Route path="/subscription/verify/:reference" element={<SubscriptionVerify />} />

          {/* Digital Download Route - Public */}
          <Route path="/download/:accessToken" element={<DigitalDownload />} />

          {/* Public merchant-issued invoice payment view (token-gated).
              Customer URL is served from pay.userevstar.com (prod) and
              pay.staging.userevstar.com (staging); `pay` is reserved
              above so those hosts skip the storefront branch and fall
              into this main routes block.
              Also kept inside SubdomainStorefront so a link sent from
              a merchant's customer storefront context still resolves. */}
          <Route path="/invoice/:invoiceRef" element={<InvoiceRoute />} />

          {/* Shared Collection Route - Public with token */}
          <Route path="/share/collection/:token" element={<SharedCollectionPage />} />

          {/* Receipt Routes - Public with token */}
          <Route path="/receipt/:orderId" element={<ReceiptPage />} />

          {/* WhatsApp Order Route - Public */}
          <Route path="/wa/:whatsappOrderId" element={<WhatsAppOrderPage />} />

          {/* Customer Routes */}
          <Route path="/customer/login" element={<CustomerAuth />} />
          <Route path="/customer/dashboard/*" element={<CustomerDashboard />} />

          {/* Marketplace path fallback */}
          <Route path="/marketplace" element={<MarketplaceHomePage />} />
          <Route path="/marketplace/merchants" element={<MarketplaceMerchantsPage />} />
          <Route path="/marketplace/merchant/:slug" element={<MarketplaceMerchantPage />} />
          <Route path="/marketplace/product/:storeSlug/:productSlug" element={<MarketplaceProductPage />} />

          {/* Checkout Route - Public (for when customers return from customer-app login) */}
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/recover/abandoned/:id" element={<AbandonedRecoveryPage />} />

          {/* Team Invitation - Public Route (must be before protected routes) */}
          <Route path="/team/accept/:token" element={<AcceptInvitation />} />

          {/* Affiliate Portal - Public Route */}
          <Route path="/affiliate/:referralCode" element={<AffiliatePortal />} />

          {/* Marketer Portal - Public Route (marketers have own auth) */}
          <Route path="/marketer" element={<MarketerPortal />} />

          {/* Knowledge Base - Public Routes (SEO-indexed help center) */}
          <Route path="/knowledgebase" element={<KnowledgeBase />} />
          <Route path="/knowledgebase/:slug" element={<KnowledgeBaseArticle />} />

        {/* Protected Routes */}
      <Route path="/create-store" element={<StoreComponent />} />
      <Route path="/*" element={<ProtectedRoutes />} />

          {/* Default Routes */}
      <Route path="/" element={<Navigate to="/login" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
    </>
  );
};

// Main App component - check subdomain/custom domain BEFORE any auth providers
function App() {
  // Check if we're on a subdomain OR custom domain FIRST, before any auth providers
  const marketplaceHost = isMarketplaceHost();
  const subdomainSlug = getSubdomainSlug();
  const customDomain = isCustomDomain();

  if (marketplaceHost) {
    return (
      <ErrorBoundary>
        <BrowserRouter>
          <ToastProvider>
            <ConfirmProvider>
              <MarketplaceShell />
            </ConfirmProvider>
          </ToastProvider>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  console.log('App: subdomainSlug detected at top level:', subdomainSlug);
  console.log('App: customDomain detected at top level:', customDomain);

  // If on subdomain OR custom domain, render storefront with CustomerAuthProvider (no merchant auth)
  if (subdomainSlug || customDomain) {
    return (
      <ErrorBoundary>
        <BrowserRouter>
          <ToastProvider>
            <ConfirmProvider>
              <CustomerAuthProvider>
                <SubdomainStorefront />
              </CustomerAuthProvider>
            </ConfirmProvider>
          </ToastProvider>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }

  // If NOT on subdomain/custom domain, render merchant app with SecureAuthProvider
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider>
          <ConfirmProvider>
            <SecureAuthProvider>
              <CurrencyProvider>
                <AppContent />
              </CurrencyProvider>
            </SecureAuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
